import moment from "moment";
import momentTimezone from "moment-timezone";

export const isBefore = (date, target, granularity) => {
  if (date) {
    return moment(date).isBefore(target, granularity);
  }
  return null;
};

export const toMomentObj = date => {
  if (date) {
    return moment(date);
  }
  return null;
};

export const toLocalDateTime = date => {
  if (date) {
    return moment(date).format("YYYY-MM-DD 00:00:00");
  }
  return null;
};

export const toYMDHm = date => {
  if (date) {
    return moment(date).format("YYYY-MM-DD HH:mm");
  }
  return null;
};

export const toYMDHm_jp = date => {
  if (date) {
    return moment(date).format("YYYY年MM月DD日 HH:mm");
  }
  return null;
};

export const timestampToYMDHm_jp = timestamp => {
  if (timestamp) {
    return moment(timestamp, "X").format("YYYY年MM月DD日 HH:mm");
  }
  return null;
};

export const toYMD = (date, format) => {
  if (date) {
    let _format = format === null ? "YYYY-MM-DD" : format;
    return moment(date).format(_format);
  }
  return null;
};

export const calendar_ja = date => {
  if (date) {
    return new Intl.DateTimeFormat("ja-JP-u-ca-japanese", {
      era: "long",
      year: "numeric",
    }).format(date);
  }
  return null;
};

export const toYMD_ja = date => {
  if (date) {
    return moment(date).format("YYYY年MM月DD日");
  }
  return null;
};

export const toDate = string => {
  if (string) {
    return moment(string).toDate();
  }
  return null;
};

export const toEMD_ja = date => {
  if (date) {
    const result = new Intl.DateTimeFormat("ja-JP-u-ca-japanese", {
      era: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
    return result;
  }
  return null;
};

export const subtractMonthFromToday = months => {
  if (months) {
    return moment().subtract(months, "months");
  }
  return null;
};

export const diffDays = targetDate => {
  if (targetDate) {
    return moment().diff(targetDate, "days");
  }
  return null;
};

export const diffHoursToFrom = (to, from) => {
  return from.diff(to, "hours");
};

export const toHmsFromSec = t => {
  let hms = "";
  let h = (t / 3600) | 0;
  let m = ((t % 3600) / 60) | 0;
  let s = t % 60;

  if (h != 0) {
    hms = h + "時間" + padZero(m) + "分" + padZero(s) + "秒";
  } else if (m != 0) {
    hms = m + "分" + padZero(s) + "秒";
  } else {
    hms = s + "秒";
  }

  return hms;

  function padZero(v) {
    if (v < 10) {
      return "0" + v;
    } else {
      return v;
    }
  }
};

export const elapsedTimeByTimestamp = timestamp => {
  return elapsedTime(moment(Math.floor(timestamp / 1000), "X").format());
};

export const elapsedTime = startTime => {
  let x = new Date(startTime);
  let now = new Date();
  let timeDiff = now - x;
  timeDiff /= 1000;

  let seconds = Math.round(timeDiff);
  timeDiff = Math.floor(timeDiff / 60);

  let minutes = Math.round(timeDiff % 60);
  timeDiff = Math.floor(timeDiff / 60);

  let hours = Math.round(timeDiff % 24);
  timeDiff = Math.floor(timeDiff / 24);

  let days = Math.round(timeDiff % 365);
  timeDiff = Math.floor(timeDiff / 365);

  let years = timeDiff;

  if (years > 0) {
    return years + (years > 1 ? "年" : "年") + "前";
  } else if (days > 0) {
    return days + (days > 1 ? "日" : "日") + "前";
  } else if (hours > 0) {
    return hours + (hours > 1 ? "時間" : "時間") + "前";
  } else if (minutes > 0) {
    return minutes + (minutes > 1 ? "分" : "分") + "前";
  } else if (seconds > 0) {
    return seconds + (seconds > 1 ? "秒" : "秒") + "前";
  } else {
    return "たった今";
  }
};

export const secToTime = seconds => {
  let accumulatedSeconds = { hour: "-", min: "-" };
  if (seconds) {
    const hour = Math.floor(seconds / 3600);
    const min = Math.floor((seconds % 3600) / 60);
    let hh;
    if (hour < 100) {
      hh = `00${hour}`.slice(-2);
    } else {
      hh = String(hour);
    }
    const mm = `00${min}`.slice(-2);
    accumulatedSeconds = { hour: hh, min: mm };
  }
  return accumulatedSeconds;
};

/**
 * 指定されたタイムゾーンで日付をフォーマットする
 * @param {Date|String} date - フォーマットする対象の日付（Dateオブジェクトまたは日付文字列）
 * @param {String} dateFormat - 出力する日付のフォーマット（例: 'YYYY-MM-DD HH:mm:ss'）
 * @param {String} [timezone='UTC'] - タイムゾーン名（例: 'Asia/Tokyo'、デフォルトは 'UTC'）
 * @returns {String|null} - 指定されたフォーマットの日付文字列、無効な入力の場合はnull
 */
export const formatDateWithTimezone = (date, dateFormat, timezone = "UTC") => {
  if (!date || !dateFormat) {
    return null;
  }

  return momentTimezone(date)
    .tz(timezone)
    .format(dateFormat);
};

/**
 * 日付設定に基づいて日付をフォーマットする
 * @param {Object} dateConfig - 日付設定
 * @param {String} dateConfig.formula - 操作タイプ ('add', 'subtract', 'specific')
 * @param {Object} dateConfig.offset - オフセット ({ years: 0, months: 0, days: 0 })
 * @param {Object} dateConfig.specificDate - 固定日付 ({ year: 2024, month: 1, day: 1 })
 * @param {Boolean} dateConfig.isStartOfMonth - 月の初日に設定
 * @param {Boolean} dateConfig.isEndOfMonth - 月の末日に設定
 * @returns {String|null} - フォーマット済み日付文字列 ('YYYY-MM-DD')、無効な場合は null
 */
export const formatDateByConfig = dateConfig => {
  if (!dateConfig) return null;

  const {
    formula,
    offset,
    specificDate,
    isStartOfMonth,
    isEndOfMonth,
  } = dateConfig;
  let date;

  if (formula === "specific" && specificDate) {
    // 固定日付を設定
    date = moment({
      year: specificDate.year,
      month: specificDate.month - 1, // momentは月が0から始まる
      day: specificDate.day,
    });
  } else if (formula === "add" || formula === "subtract") {
    const factor = formula === "add" ? 1 : -1;
    date = moment()
      .add(factor * (offset.years || 0), "years")
      .add(factor * (offset.months || 0), "months")
      .add(factor * (offset.days || 0), "days");
  } else {
    date = moment();
  }

  if (isStartOfMonth) {
    date.startOf("month");
  } else if (isEndOfMonth) {
    date.endOf("month");
  }

  return date.format("YYYY-MM-DD");
};

/**
 * 当年度を取得する（4月〜翌3月。例: 2026年2月 → 2025年度）
 * @param {Date} [date] - 基準日。省略時は現在日時
 * @returns {number} 当年度の年（西暦）
 */
export const getCurrentFiscalYear = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return month >= 4 ? year : year - 1;
};

export default {
  toMomentObj,
  toLocalDateTime,
  toYMDHm,
  toYMDHm_jp,
  timestampToYMDHm_jp,
  toYMD,
  toYMD_ja,
  toDate,
  toEMD_ja,
  subtractMonthFromToday,
  diffDays,
  diffHoursToFrom,
  toHmsFromSec,
  elapsedTime,
  elapsedTimeByTimestamp,
  calendar_ja,
  isBefore,
  secToTime,
  formatDateWithTimezone,
  formatDateByConfig,
  getCurrentFiscalYear,
};
